import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { AccountSummaryDto, PagedResult } from "@/lib/clients-accounts";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";

export interface AccountPickerProps {
  /** The clientId to scope the account list. When null/undefined the picker is disabled. */
  clientId: number | null | undefined;
  value: number | null;
  onChange: (accountId: number | null) => void;
  placeholder?: string;
}

export function AccountPicker({
  clientId,
  value,
  onChange,
  placeholder,
}: AccountPickerProps) {
  const [q, setQ] = useState("");
  const disabled = clientId == null;

  const { data } = useQuery({
    queryKey: ["account-picker", clientId, q],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("clientId", String(clientId));
      params.set("pageSize", "20");
      if (q.trim().length > 0) params.set("q", q.trim());
      return apiFetch<PagedResult<AccountSummaryDto>>(`/api/accounts?${params.toString()}`);
    },
    enabled: !disabled,
    staleTime: 5000,
    select: (res) => res.items,
  });

  // Find the currently selected account in the fetched list for display.
  const selectedAccount = data?.find((a) => a.accountId === value) ?? null;

  if (disabled) {
    return (
      <div
        className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 text-sm text-muted-foreground"
        aria-disabled="true"
      >
        {placeholder ?? "Select a client first"}
      </div>
    );
  }

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder={placeholder ?? "Search accounts..."}
        value={q}
        onValueChange={setQ}
      />
      <CommandList>
        {data && data.length === 0 && <CommandEmpty>No accounts found</CommandEmpty>}
        {data?.map((a) => (
          <CommandItem key={a.accountId} onSelect={() => onChange(a.accountId)}>
            <span className="font-mono">{a.accountReferenceNo}</span>
            {a.companyName && (
              <span className="ml-2 text-muted-foreground">({a.companyName})</span>
            )}
            <span className="ml-auto text-xs text-muted-foreground">
              {a.accountStatusCode}
            </span>
          </CommandItem>
        ))}
      </CommandList>
      {value !== null && (
        <div className="px-3 py-2 text-sm">
          Selected account:{" "}
          <strong>
            {selectedAccount?.accountReferenceNo ?? `#${value}`}
          </strong>
          <button className="ml-2 underline" onClick={() => onChange(null)}>
            Clear
          </button>
        </div>
      )}
    </Command>
  );
}
